<ng-container *ngIf="formGroup">
    <div [ngStyle]="init ? {'visibility':'visible'} : {'visibility':'collapse'}">
        <form [formGroup]="formGroup" class="form p-16">
            <div fxLayout="row">
                <mat-slide-toggle formControlName="useDynamicPrice">{{ 'workflows.screens.productDynamicPrice.useDynamicPrice' | translate }}</mat-slide-toggle>
            </div>

            <ng-container *ngIf="useDynamicPrice && hasCriteriaWidgets">
                <div class="mt-24" fxLayout="column">

                    <ng-container *ngIf="requirements.includes('boat')">
                        <form-widget-boat [appearance]="'outline'"
                                          [selectBoat]="boat"
                                          [givenBoats]="boats"
                                          [boatRequired]="true"
                                          [boatOwnerName]="dynamicPriceContext?.person?.toString() || dynamicPriceContext?.company?.toString()"
                                          [person$]="person$"
                                          [company$]="company$"
                                          [slimmedView]="true"
                                          [canCreate]="false"></form-widget-boat>
                    </ng-container>

                    <ng-container *ngIf="requirements.includes('berth')">
                        <form-widget-berth-only [parentFormGroup]="formGroup"
                                                [appearance]="'outline'"
                                                [selectBerth]="berth"
                                                [givenBerths]="berths"
                                                [berthRequired]="true"
                                                [personOrCompanyName]="dynamicPriceContext?.person?.toString() || dynamicPriceContext?.company?.toString()"
                                                [slimmedView]="true"></form-widget-berth-only>
                    </ng-container>

                    <ng-container *ngIf="requirements.includes('pier')">
                        <form-widget-pier [parentFormGroup]="formGroup"
                                          [appearance]="'outline'"
                                          [selectPier]="pier"
                                          [givenPiers]="piers"
                                          [pierRequired]="true"
                                          [slimmedView]="true"></form-widget-pier>
                    </ng-container>

                    <ng-container *ngIf="requirements.includes('tenantRelation')">
                        <form-widget-tenant-relation [parentFormGroup]="formGroup"
                                                     [selectTenantRelation]="tenantRelation"
                                                     [givenTenantRelations]="tenantRelations"
                                                     [allowedTenantRelationIds]="relevantTenantRelationIds"
                                                     [tenantRelationRequired]="true"
                                                     [personOrCompanyName]="dynamicPriceContext?.person?.toString() || dynamicPriceContext?.company?.toString()"
                                                     [slimmedView]="true"></form-widget-tenant-relation>
                    </ng-container>

                    <ng-container *ngIf="requirements.includes('season')">
                        <form-widget-season (seasonChangedManually)="this.seasonChangedManually = true;"
                                            [parentFormGroup]="formGroup"
                                            [appearance]="'outline'"
                                            [selectSeason]="season"
                                            [seasonRequired]="true"
                                            [slimmedView]="true"></form-widget-season>
                    </ng-container>

                    <ng-container *ngIf="requirements.includes('winterStorage')">
                        <form-widget-winter-storage [topListLabel]="'workflows.formWidgets.winterStorage.currentSlotTopList' | translate"
                                                    [parentFormGroup]="formGroup"
                                                    [appearance]="'outline'"
                                                    [selectWinterStorage]="winterStorage"
                                                    [givenWinterStorages]="winterStorages"
                                                    [winterStorageRequired]="true"
                                                    [slimmedView]="true"></form-widget-winter-storage>
                    </ng-container>

                </div>
            </ng-container>
        </form>

        <!-- Without this the panel is a bare toggle whenever the rules yield nothing - no fields, no
             price, no reason given. (#6944) -->
        <ng-container *ngIf="useDynamicPrice && stateMessageKey as messageKey">
            <div class="info-box p-16">
                <u2b-message type="warning">{{ messageKey | translate }}</u2b-message>
            </div>
        </ng-container>

        <!-- v2 groups engine: per-group breakdown table. Presence of ruleCalculations.groups is the
             discriminator — legacy snapshots never set this field. -->
        <ng-container
                *ngIf="useDynamicPrice && dynamicPrice?.ruleCalculations?.groups?.length > 0">
            <div class="info-box p-16">
                <u2b-message type="info">
                <table class="dyn-price-groups-table">
                    <thead>
                    <tr>
                        <th>{{ 'workflows.screens.productDynamicPrice.groups.colGroup' | translate }}</th>
                        <th class="col-base">{{ 'workflows.screens.productDynamicPrice.groups.colBase' | translate }}</th>
                        <th>{{ 'workflows.screens.productDynamicPrice.groups.colFactors' | translate }}</th>
                        <th>{{ 'workflows.screens.productDynamicPrice.groups.colCondition' | translate }}</th>
                        <th class="col-value">{{ 'workflows.screens.productDynamicPrice.groups.colValue' | translate }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let group of dynamicPrice.ruleCalculations.groups">
                        <td>
                            <ng-container *ngIf="group.isImplicitFallback; else groupNameCell">
                                <em>{{ 'workflows.screens.productDynamicPrice.groups.implicitFallback' | translate }}</em>
                            </ng-container>
                            <ng-template #groupNameCell>{{ group.name || ('workflows.screens.productDynamicPrice.groups.unnamedGroup' | translate) }}</ng-template>
                        </td>
                        <td class="col-base">{{ group.basePrice | bcmDynamicCurrency }}</td>
                        <td>
                            <ng-container *ngIf="group.calculations?.length; else noFactors">
                                <span *ngFor="let c of group.calculations">
                                    × {{ c.givenValue | number: '1.2-2' }} ({{ c.conditionEntity }})<br>
                                </span>
                            </ng-container>
                            <ng-template #noFactors>{{ 'workflows.screens.productDynamicPrice.groups.noFactors' | translate }}</ng-template>
                        </td>
                        <td>
                            <ng-container *ngIf="group.evaluations?.length; else alwaysActive">
                                <span *ngFor="let e of group.evaluations">
                                    {{ e.conditionEntity }} {{ e.conditionOperatorText }} {{ e.requiredValue }}
                                    <ng-container *ngIf="e.isTrue; else condFail">
                                        <mat-icon class="icon-check condition-marker">check_circle</mat-icon>
                                    </ng-container>
                                    <ng-template #condFail>
                                        <mat-icon class="icon-warning condition-marker">cancel</mat-icon>
                                    </ng-template>
                                    <br>
                                </span>
                            </ng-container>
                            <ng-template #alwaysActive>{{ 'workflows.screens.productDynamicPrice.groups.alwaysActive' | translate }}</ng-template>
                        </td>
                        <td class="col-value group-price">{{ group.groupPrice | bcmDynamicCurrency }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td class="total-label" colspan="4">{{ 'workflows.screens.productDynamicPrice.groups.total' | translate }}</td>
                        <td class="total-price">{{ dynamicPrice?.rulePrice | bcmDynamicCurrency }}</td>
                    </tr>
                    </tfoot>
                </table>
                </u2b-message>
            </div>
        </ng-container>

        <!-- `ruleIndex >= 0` as well as the row count: a catch-all rule set (`rules: []`) matches and
             prices the position but produces no evaluation rows, and hiding the whole box for it left
             the user with a bare toggle and no idea which rule set the price came from. (#6944) -->
        <ng-container
                *ngIf="useDynamicPrice && !dynamicPrice?.ruleCalculations?.groups && dynamicPrice?.ruleEvaluations && (dynamicPrice?.ruleIndex >= 0 || dynamicPrice?.ruleEvaluations.evaluations?.length > 0)">
            <div class="info-box p-16">

                <ng-container *ngIf="initIndexChanged || initTitleChanged || initPriceChanged">
                    <u2b-message type="warning">
                        <div class="mb-16">{{ 'workflows.screens.productDynamicPrice.priceChangedIntro' | translate }}</div>
                        <ng-container *ngIf="initIndexChanged">
                            <div [innerHTML]="'workflows.screens.productDynamicPrice.oldRuleLineHtml' | translate: { index: dynamicPriceOld.ruleIndex + 1 }"></div>
                        </ng-container>
                        <ng-container *ngIf="initTitleChanged">
                            <div fxLayout="row">
                                <div style="width: 200px">{{ 'workflows.screens.productDynamicPrice.oldRuleNameLabel' | translate }}</div>
                                <div style="margin-left: auto; font-weight: bold; text-align: justify;">
                                    {{ dynamicPriceOld.ruleName }}
                                </div>
                            </div>
                        </ng-container>
                        <ng-container *ngIf="initPriceChanged">
                            <div fxLayout="row">
                                <div style="width: 200px">{{ 'workflows.screens.productDynamicPrice.oldRulePriceLabel' | translate }}</div>
                                <div style="margin-left: auto; font-weight: bold;">
                                    {{ dynamicPriceOld?.ruleEvaluations.price | bcmDynamicCurrency }}
                                </div>
                            </div>
                        </ng-container>
                    </u2b-message>
                </ng-container>

                <u2b-message type="info">
                    <div [innerHTML]="'workflows.screens.productDynamicPrice.setByRuleHtml' | translate: { index: dynamicPrice.ruleIndex + 1 }"></div>
                    <div class="mt-16" fxLayout="row">
                        <div style="width: 200px">{{ 'workflows.screens.productDynamicPrice.ruleNameLabel' | translate }}</div>
                        <div style="margin-left: auto; font-weight: bold; text-align: justify;">
                            {{ dynamicPrice.ruleName }}
                        </div>
                    </div>
                    <div fxLayout="row">
                        <div style="width: 200px">{{ 'workflows.screens.productDynamicPrice.rulePriceLabel' | translate }}</div>
                        <div style="margin-left: auto; font-weight: bold;">
                            {{ dynamicPrice?.ruleEvaluations.price | bcmDynamicCurrency }}
                        </div>
                    </div>
                </u2b-message>
                <table class="mt-8" *ngIf="dynamicPrice?.ruleEvaluations.evaluations?.length > 0">
                    <thead>
                    <td class="text-bold">{{ 'workflows.screens.productDynamicPrice.tableEntity' | translate }}</td>
                    <td class="text-bold">{{ 'workflows.screens.productDynamicPrice.tableEntityValue' | translate }}</td>
                    <td class="text-bold">{{ 'workflows.screens.productDynamicPrice.tableOperator' | translate }}</td>
                    <td class="text-bold">{{ 'workflows.screens.productDynamicPrice.tableRuleValue' | translate }}</td>
                    <td class="text-bold"></td>
                    </thead>
                    <tbody>
                    <tr *ngFor="let evaluation of dynamicPrice.ruleEvaluations.evaluations">
                        <td>{{ evaluation.conditionEntity }}</td>
                        <td>{{ evaluation.givenValue }}</td>
                        <td>{{ evaluation.conditionOperatorText }}</td>
                        <td>{{ evaluation.requiredValue }}</td>
                        <td>
                            <div style="display: flex">
                                <ng-container *ngIf="evaluation.isTrue; else notTrue">
                                    <mat-icon class="icon-check">
                                        check_circle
                                    </mat-icon>
                                </ng-container>
                                <ng-template #notTrue>
                                    <mat-icon class="icon-warning">
                                        cancel
                                    </mat-icon>
                                </ng-template>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>

            </div>
        </ng-container>

        <ng-container
                *ngIf="useDynamicPrice && !dynamicPrice?.ruleCalculations?.groups && dynamicPrice?.ruleCalculations && dynamicPrice?.ruleCalculations.calculations?.length > 0">
            <div class="info-box p-16">
                <u2b-message type="info">
                    {{ 'workflows.screens.productDynamicPrice.formulasApplied' | translate }}<br>
                    <br>
                    <div *ngFor="let calculation of dynamicPrice.ruleCalculations.calculations">
                        <span>{{ calculation.priceBeforeCalculation | bcmDynamicCurrency }} {{ calculation.conditionOperatorSymbol }} {{ calculation.givenValue | number: '1.2-2' }}
                            ({{ calculation.conditionEntity }})</span>
                        <span style="float: right">= {{ calculation.priceAfterCalculation | bcmDynamicCurrency }}</span><br>
                    </div>
                    <br>
                    <div style="border-top: 1px solid black">{{ 'workflows.screens.productDynamicPrice.priceAfterCalculation' | translate }} <span
                            style="float:right; font-weight: bold">{{ dynamicPrice?.ruleCalculations.price | bcmDynamicCurrency }}</span>
                    </div>
                </u2b-message>
            </div>
        </ng-container>
    </div>
</ng-container>
